import * as React from 'react';
import Layout from "../../../Components/layouts";
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { ReportServices } from '../../../Services/ReportServices';
import CustomTabs from '../../../Components/CustomTabs';
import { RouteSkeleton } from '../../../Components/RouteTransition';
import { pageEyebrowStyles, pageTitleTextStyles, surfaceCardStyles } from '../../../theme/commonStyles';
import {
    RespondentDigitalTalentMaturityColumn,
} from '../../../utils/constants';
import { notify, saveJsonAsCsv } from 'utils';
import jsPDF from 'jspdf';
import GaugeDashboard from '../component/DigitalMaturityInsights/dynamicReport';
import DetailsAnalyticsTabData from '../component/DigitalMaturityInsights/DyanmicTable';
import useRoutedTab from '../../../hooks/useRoutedTab';

const reportServices = new ReportServices();
const TAB_ROUTE_SLUGS = ['respondents', 'insights'];
const RESPONDENTS_TAB = 0;

const useStyles = makeStyles((theme) => ({
    page: {
        width: '100%',
        padding: 0,
    },
    pageHeader: {
        marginBottom: theme.spacing(2.5),
    },
    pageEyebrow: {
        ...pageEyebrowStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageTitle: {
        ...pageTitleTextStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageDescription: {
        color: '#647184',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        maxWidth: 640,
    },
    panel: {
        ...surfaceCardStyles,
        padding: theme.spacing(2, 2.25),
        boxSizing: 'border-box',
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1.5),
        },
    },
}));

const DigitalTalentMaturity = () => {
    const classes = useStyles();
    const [activeTab, selectTab] = useRoutedTab(TAB_ROUTE_SLUGS);
    const [initialLoading, setInitialLoading] = React.useState(true);
    const respondentsRequestedRef = React.useRef(false);

    const [analyticsFilterState, setAnalyticsFilterState] = React.useState({
        users: [],
        customer: [],
        project: [],
        pageno: 0,
        perpage: 10
    });
    const [loading, setLoading] = React.useState(false);
    const [projectsReportData, setProjectsReportData] = React.useState([]);
    const [filters, setFilters] = React.useState({
        users: [],
        customer: [],
        project: [],
        pagination: {
            previous_page: null,
            is_previous_page: false,
            next_page: null,
            is_next_page: false,
            start_index: 0,
            end_index: 0,
            total_entries: 0,
            total_pages: 0,
            page: 1,
        }
    });
    const [, setDownloadReportLoading] = React.useState(false);
    const [, setPrintReportLoading] = React.useState(false);

    const getProjectReportData = async (updatedFilter = analyticsFilterState) => {
         setLoading(true);

    const params = new URLSearchParams();

    // Map to value for API
    const customerValues = (updatedFilter.customer || []).map(c => c.value);
    const projectValues = (updatedFilter.project || []).map(p => p.value);
    const userValues = (updatedFilter.users || []).map(u => u.value);

    if (customerValues.length) params.append('company_name', customerValues.join(','));
    if (projectValues.length) params.append('project', projectValues.join(','));
    if (userValues.length) params.append('email', userValues.join(','));
    params.append('page', updatedFilter.pageno + 1);
    params.append('limit', updatedFilter.perpage);

    let result = await reportServices.getDataTalentMaturityModelData(
        `main/user-survey-averages?${params.toString()}`
    );

        if (result && result.status) {
            setFilters({
                ...result.data.filter,
                pagination: result.data.pagination
            });
            setProjectsReportData(result.data.results || []);
        } else {
            setFilters({
                users: [],
                customer: [],
                project: [],
                pagination: {
                    previous_page: null,
                    is_previous_page: false,
                    next_page: null,
                    is_next_page: false,
                    start_index: 0,
                    end_index: 0,
                    total_entries: 0,
                    total_pages: 0,
                    page: 1,
                }
            });
            setProjectsReportData([]);
        }
        setLoading(false);
    };

    React.useEffect(() => {
        if (activeTab !== RESPONDENTS_TAB) {
            setInitialLoading(false);
            return undefined;
        }
        if (respondentsRequestedRef.current) return undefined;
        respondentsRequestedRef.current = true;
        let active = true;
        getProjectReportData()
            .catch(() => undefined)
            .finally(() => {
                if (active) setInitialLoading(false);
            });
        return () => { active = false; };
        // eslint-disable-next-line
    }, [activeTab]);

    const handlePageChange = (page) => {
        setAnalyticsFilterState({ ...analyticsFilterState, pageno: page - 1 });
        getProjectReportData({ ...analyticsFilterState, pageno: page - 1 });
    };

    const handleChange = (val, type) => {
        setAnalyticsFilterState({ ...analyticsFilterState, [type]: val });
        getProjectReportData({ ...analyticsFilterState, [type]: val, pageno: 0 });
    };

    const handleSubmitFilter = () => {
        getProjectReportData({ ...analyticsFilterState, pageno: 0 });
    };

    const handleClearFilters = () => {
        const cleared = {
            users: [],
            customer: [],
            project: [],
            pageno: 0,
            perpage: 10,
        };
        setAnalyticsFilterState(cleared);
        getProjectReportData(cleared);
    };

  const handleDownload = async () => {
    let fields = {};
    let columns = RespondentDigitalTalentMaturityColumn;
    // Transform filters for API
    let reportFilter = {
        ...analyticsFilterState,
        customer: (analyticsFilterState.customer || []).map(c => c.value),
        project: (analyticsFilterState.project || []).map(p => p.value),
        users: (analyticsFilterState.users || []).map(u => u.value),
        perpage: 99999,
        pageno: 0
    };
    columns.forEach((itm) => {
        fields[itm.key] = itm.label;
    });
    setDownloadReportLoading(true);
    let result = await reportServices.getDownloadCsvReportData(`main/download-user-survey-averages`, reportFilter);
    if (result && result.status && result?.data?.data) {
        saveJsonAsCsv({ data: result.data.data, fields, filename: 'dtmreportdata' });
    } else {
        notify('error', "Report data not found!");
    }
    setDownloadReportLoading(false);
};

const handlePrint = async () => {
    // Transform filters for API
    let reportFilter = {
        ...analyticsFilterState,
        customer: (analyticsFilterState.customer || []).map(c => c.value),
        project: (analyticsFilterState.project || []).map(p => p.value),
        users: (analyticsFilterState.users || []).map(u => u.value),
        pageno: 0,
        perpage: 999999
    };
    setPrintReportLoading(true);

    if (!reportFilter.customer?.length) {
        notify('error', "Please select customer!");
        setPrintReportLoading(false);
        return;
    }

    try {
        const result = await reportServices.getDownloadCsvReportData(`main/download-user-survey-averages`, reportFilter);

        if (result && result.status && result?.data?.data) {
            const pdf = new jsPDF("p", "pt", "a3");

            const headers = [
                "Customer",
                "Project",
                "Name",
                "Surname",
                "Username",
                "Organizational Positioning",
                "Organizational Development",
                "Talent Readiness and Mobility",
                "Continuous Learning",
                "Transformative Environment",
                "Intelligent Harmony"
            ];

            const rows = result.data.data.map(item => [
                item.company_name || 'N/A',
                item.project || 'N/A',
                item.firstname || 'N/A',
                item.lastname || 'N/A',
                item.email || 'N/A',
                item.g1_average || 'N/A',
                item.g2_average || 'N/A',
                item.g3_average || 'N/A',
                item.g4_average || 'N/A',
                item.g5_average || 'N/A',
                item.g6_average || 'N/A',
            ]);

            pdf.autoTable({
                head: [headers],
                body: rows,
                startY: 65,
                theme: "grid",
                styles: { fontSize: 8 }
            });

            pdf.save('DigitalTalentMaturityReport.pdf');
        } else {
            notify('error', "Report data not found!");
        }
    } catch (error) {
        notify('error', "Something went wrong while generating the PDF!");
        console.error(error);
    }

    setPrintReportLoading(false);
};

    return (
        <Layout>
            {initialLoading ? (
                <RouteSkeleton variant="maturity-report" />
            ) : (
            <main className={classes.page}>
                <div className={classes.pageHeader}>
                    <Typography className={classes.pageEyebrow}>Reporting</Typography>
                    <Typography className={classes.pageTitle}>Digital Talent Maturity</Typography>
                    <Typography className={classes.pageDescription}>
                        Review respondent results and insights for digital talent maturity assessments.
                    </Typography>
                </div>
                <div className={classes.panel}>
                    <CustomTabs
                        activeIndex={activeTab}
                        handleChangeActiveNotiTab={(_evt, index) => selectTab(index)}
                        disableSwipe
                        lazyMount
                        tabs={[
                            {
                                label: 'Respondents',
                                children: (
                                    <DetailsAnalyticsTabData
                                        type='respondant'
                                        title="Digital Talent Maturity Model Assessment"
                                       filters={{
        ...filters,
        users: filters.users?.map(u => ({ label: u, value: u })) || [],
        customer: filters.customer?.map(c => ({ label: c, value: c })) || [],
        project: filters.project?.map(p => ({ label: p, value: p })) || [],
    }}
                                        allFilterState={analyticsFilterState}
                                        handleChange={handleChange}
                                        handleSubmitFilter={handleSubmitFilter}
                                        handleClearFilters={handleClearFilters}
                                        columns={RespondentDigitalTalentMaturityColumn}
                                        loading={loading}
                                        list={projectsReportData}
                                        handlePageChange={handlePageChange}
                                        handleDownload={handleDownload}
                                        handlePrint={handlePrint}
                                        pageName="readline"
                                        noDataMessage="No record found"
                                    />
                                )
                            },
                            {
                                label: 'Insights',
                                children: (
                                    <GaugeDashboard />
                                )
                            },
                        ]}
                    />
                </div>
            </main>
            )}
        </Layout>
    );
};

export default DigitalTalentMaturity;
